import React, { useState, useEffect, useRef } from "react";
import { Formik, Form } from "formik";
import { Button } from "devextreme-react/button";
import { SelectBox, DateBox, TextBox } from "devextreme-react";
import INTAKE_API from "api/intakeApi";
import { intakeSchema } from "./intakeSchema";
import { useNavigate, useParams } from "react-router-dom";
import { Toast } from "devextreme-react/toast";
import { useFetch, usePost, useUpdate } from "@hooks/useBatchIntakeApi";
import { useQueryClient } from "@tanstack/react-query";
import "../cards/DarkCard.scss";
import Loader from "@common-components/loader/Loader";

const EditBatchIntake = () => {
  const { id } = useParams();
  let storageid = localStorage.getItem("selectedRowKey"); // Store the ID in localStorage for later use
  console.log("storageid", storageid);
  // Fix: Only use id if defined, otherwise use storageid
  const shouldFetchEditData = id || storageid;
  const intakeIdParam = id ? id : storageid ? storageid : undefined;

  const { data: editData, isLoading: isEditDataLoading } = useFetch(
    shouldFetchEditData ? "getSinglebatchIntake" : null,
    INTAKE_API.batchIntake,
    intakeIdParam ? { IntakeId: intakeIdParam } : undefined
  );
  const { data: statuses } = useFetch("getStatus", INTAKE_API.getStatus, {
    PageNo: 0,
    PageSize: 1000,
  });
  const { data: suppliers } = useFetch(
    "getSuppliers",
    INTAKE_API.getSuppliers,
    { PageNo: 0, PageSize: 1000 }
  );
  const [visible, setVisible] = useState(false);
  const [toastType, setToastType] = useState("error");
  const [toastMessage, setToastMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const [supplierID, setSupplierID] = useState(null);
  const [productID, setProductID] = useState(null);

  const showToast = (type, message) => {
    setToastType(type);
    setToastMessage(message);
    setVisible(true);
  };

  const { data: products } = useFetch(
    "getProducts",
    INTAKE_API.getProducts,
    { PageNo: 0, PageSize: 1000 } // Removed supplierID dependency
  );

  const { data: sizes } = useFetch(
    productID ? `getProductsSize_${productID}` : null,
    INTAKE_API.getProductsSize,
    productID ? { ProductId: productID } : undefined
  );

  const { data: qualities } = useFetch(
    productID ? `getProductsQuality_${productID}` : null,
    INTAKE_API.getProductsQuality,
    productID ? { ProductId: productID } : undefined
  );

  const setFieldValueRef = useRef(null);

  useEffect(() => {
    if (editData?.data && suppliers?.data) {
      console.log("editData:", editData); // Debug log to check editData structure
      console.log("suppliers:", suppliers.data); // Debug log to check suppliers data

      setSupplierID(editData.data.supplier?.supplierID || null);
      setProductID(editData.data.product?.productID || null);

      // Find the supplier matching the ID from params
      const matchingSupplier = suppliers.data.find(
        (supplier) => supplier.supplierID === editData.data.supplier?.supplierID
      );
      console.log("Matching Supplier:", matchingSupplier); // Debug log to check matching supplier
      // Populate availableLocations from the matching supplier
      const locations = matchingSupplier?.supplierAddress || [];
      console.log("Available Locations:", locations); // Debug log to check locations
      setFieldValueRef.current?.("availableLocations", locations); // Set available locations
    }
  }, [editData, suppliers]);

  useEffect(() => {
    if (sizes?.data?.length > 0 && setFieldValueRef.current) {
      setFieldValueRef.current("productSizeID", sizes.data[0].productSizeID);
    }
  }, [sizes]);

  useEffect(() => {
    if (qualities?.data?.length > 0 && setFieldValueRef.current) {
      setFieldValueRef.current(
        "productQualityID",
        qualities.data[0].productQualityID
      );
    }
  }, [qualities]);

  const batchIntakeMutation = useUpdate("batchIntake", INTAKE_API.batchIntake);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  console.log("suppliers", suppliers?.data);

  // Always show loader for 2 seconds when entering the page
  const [initialLoader, setInitialLoader] = useState(true);
  useEffect(() => {
    const timer = setTimeout(() => setInitialLoader(false), 500);
    return () => clearTimeout(timer);
  }, []);

  if (initialLoader) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader />
      </div>
    );
  }

  // Add this block to show a message if no intake is selected
  if (!editData?.data) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-lg text-gray-500">No Batch Selected</p>
      </div>
    );
  }

  return (
    <>
      <Toast
        visible={visible}
        message={toastMessage}
        type={toastType} // success, error, warning, info
        displayTime={2000} // 3 seconds
        onHiding={() => setVisible(false)}
        position={{ my: "top right", at: "top right", offset: "10 10" }}
      />

      <Formik
        enableReinitialize={true} // Add this line to reinitialize Formik when initialValues change
        initialValues={{
          supplierID: editData?.data?.supplier?.supplierID || null,
          intakeDate: editData?.data?.intakeDate || "",
          statusID: editData?.data?.status?.orderStatusId || null,
          claim: editData?.data?.claim || "",
          pricePerKG: editData?.data?.pricePerKG || null,
          volume: editData?.data?.volume || null,
          claimAmount: editData?.data?.claimAmount || null,
          outputProduct: editData?.data?.outputProduct || "",
          salePricePerKG: editData?.data?.salePricePerKG || "",
          productID: editData?.data?.product?.productID || null,
          productSizeID: editData?.data?.productSize?.productSizeID || null,
          productQualityID:
            editData?.data?.productQuality?.productQualityID || null,
          locationID: editData?.data?.location?.supplierAddressID || null,
          purchaseRef: editData?.data?.purchaseRef || "",
          batchNumber: editData?.data?.batchNumber || "",
          availableLocations: [], // Initialize as empty, will be populated dynamically
        }}
        validationSchema={intakeSchema}
        onSubmit={async (values) => {
          setLoading(true); // Start loading

          // Remove empty non-required fields
          const filteredValues = Object.fromEntries(
            Object.entries(values).filter(
              ([key, value]) => value !== null && value !== ""
            )
          );

          delete filteredValues.availableLocations; // Remove this field if not needed
          console.log("submitting", filteredValues);

          try {
            // Use updateMutation with mutateAsync
            await batchIntakeMutation.mutateAsync({
              params: { IntakeID: intakeIdParam }, // Use correct param for update as well
              data: filteredValues, // ✅ Send modified values
            });

            console.log("Form Submitted Successfully");
            showToast("success", "Batch Intake Updated successfully");

            setTimeout(() => {
              navigate("/quality/fillet/overview", { state: true });
              setLoading(false); // Stop loading
            }, 2000);
          } catch (error) {
            setLoading(false); // Stop loading
            console.error("Error submitting form:", error);
            showToast("error", "Failed to update Batch Intake");
          }
        }}
      >
        {({
          values,
          handleChange,
          setFieldValue,
          errors,
          touched,
          setFieldError,
        }) => {
          console.log("Formik Values:", values); // Debug log to check form values

          setFieldValueRef.current = setFieldValue; // Store setFieldValue in the ref

          return (
            <Form className="space-y-4">
              {/* Back Button */}

              {/* Section: New Batch Intake */}
              <div className="w-full flex items-center mt-2 mb-2">
                <div className="flex items-center mb-3 mr-2">
                  <Button
                    icon="back"
                    type="normal"
                    stylingMode="text"
                    onClick={() => navigate("/quality/fillet/overview")}
                  />
                </div>
                <p className="whitespace-nowrap text-xl font-medium">
                  Edit Batch Intake:
                </p>
                <div className="flex-grow h-px bg-gray-300 ml-2 light-bg"></div>
              </div>
              <div className="dx-card p-6 custom-base-accent">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Suppliers */}
                  {/* Supplier Dropdown */}
                  <div>
                    <label className="relative">
                      Supplier{" "}
                      <span className="text-red-500 text-lg absolute top-[-4px] right-[-10px]">
                        *
                      </span>
                    </label>
                    <SelectBox
                      dataSource={suppliers?.data || []}
                      displayExpr="name"
                      valueExpr="supplierID"
                       searchEnabled={true} // Enable search
                      showClearButton={true} // Allow deselection
                      value={values.supplierID}
                      onValueChanged={(e) => {
                        setFieldValue("supplierID", e.value);

                        // Find the selected supplier
                        const selectedSupplier = suppliers?.data?.find(
                          (s) => s.supplierID === e.value
                        );

                        // Extract and set the supplierAddress list for the location dropdown
                        const locations =
                          selectedSupplier?.supplierAddress || [];
                        setFieldValue("availableLocations", locations); // Set available locations
                        setFieldValue("locationID", ""); // Reset location when supplier changes
                        // setFieldValue("productID", null); // Reset product when supplier changes

                        setSupplierID(e.value); // Set supplierID to trigger fetching products
                      }}
                      isValid={!(errors.supplierID && touched.supplierID)}
                      validationError={
                        errors.supplierID
                          ? { message: errors.supplierID }
                          : null
                      }
                    />
                  </div>

                  {/* Date */}
                  <div>
                    <label className="relative">
                      Date{" "}
                      <span className="text-red-500 text-lg absolute top-[-4px] right-[-10px]">
                        *
                      </span>
                    </label>
                    <DateBox
                      value={values.intakeDate}
                      onValueChanged={(e) =>
                        setFieldValue("intakeDate", e.value)
                      }
                      isValid={!(errors.intakeDate && touched.intakeDate)}
                      validationError={
                        errors.intakeDate
                          ? { message: errors.intakeDate }
                          : null
                      }
                      max={new Date()} // 🔹 Restricts selection to today or past dates
                      displayFormat="yyyy-MM-dd" // Optional: Ensures a consistent forma
                      openOnFieldClick={true}
                      showClearButton={true}
                      inputAttr={{ readOnly: true }} // This disables typing
                      onKeyDown={(e) => e.event?.preventDefault()} // Extra protection
                    />
                  </div>

                  {/* Status */}

                  <div>
                    <label className="relative">
                      Status{" "}
                      <span className="text-red-500 text-lg absolute top-[-4px] right-[-10px] ">
                        *
                      </span>
                    </label>
                    <SelectBox
                      displayExpr="description"
                      valueExpr="orderStatusId"
                      dataSource={
                        editData?.data
                          ? editData?.data?.qcp && editData.data?.ccp
                            ? statuses?.data
                            : (statuses?.data || []).filter(
                                (status) => status?.description !== "Completed"
                              )
                          : []
                      } // Exclude "Completed" status
                      value={values.statusID}
                      onValueChanged={(e) => setFieldValue("statusID", e.value)}
                      isValid={!(errors.statusID && touched.statusID)}
                      validationError={
                        errors.statusID ? { message: errors.statusID } : null
                      }
                    />
                  </div>
                  <div>
                    <label>Output Product</label>

                    <SelectBox
                      dataSource={[
                        { id: 1, name: "Trim A" },
                        { id: 2, name: "Trim B" },
                        { id: 3, name: "Trim C" },
                        { id: 3, name: "Trim D" },
                        { id: 3, name: "Trim E" },
                      ]}
                      displayExpr="name"
                      valueExpr="name"
                      showClearButton={true}
                      value={values.outputProduct}
                      onValueChanged={(e) =>
                        setFieldValue("outputProduct", e.value)
                      }
                      isValid={!(errors.outputProduct && touched.outputProduct)}
                      validationError={
                        errors.outputProduct
                          ? { message: errors.outputProduct }
                          : null
                      }
                    />
                  </div>
                  {/* Claim */}

                  {[
                    { name: "pricePerKG", label: "Price / kg (€)" },
                    { name: "volume", label: "Volume (kg)" },
                  ].map(({ name, label }) => (
                    <div key={name}>
                      <label className="relative">
                        {label}{" "}
                        <span className="text-red-500 text-lg absolute top-[-4px] right-[-10px]">
                          *
                        </span>
                      </label>
                      <TextBox
                        value={values[name]}
                        inputAttr={{ type: "number", step: "any", min: "1" }} // Allow decimal values
                        // Restricts to numbers only

                        onValueChanged={(e) => setFieldValue(name, e.value)}
                        isValid={!(errors[name] && touched[name])}
                        validationError={
                          errors[name] && touched[name]
                            ? { message: errors[name] }
                            : null
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Section: Volume */}
              <div className="dx-card p-6 custom-base-accent">
                <div className="row mt-4 mb-2">
                  <div className="col-md-12 flex items-center">
                    <h6 className="whitespace-nowrap">Claim:</h6>
                    <div className="flex-grow h-px bg-gray-300 ml-2"></div>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label>Claim</label>
                    <SelectBox
                      dataSource={[
                        { id: 1, name: "Dead fish upon delivery" },
                        { id: 2, name: "Soft texture and poor flesh quality" },
                        { id: 3, name: "Discoloration or melanin spots" },
                        { id: 3, name: "Sea lice and skin wounds" },
                      ]}
                      displayExpr="name"
                      valueExpr="name"
                      showClearButton={true}
                      value={values.claim}
                      onValueChanged={(e) => setFieldValue("claim", e.value)}
                      isValid={!(errors.claim && touched.claim)}
                      validationError={
                        errors.claim ? { message: errors.claim } : null
                      }
                    />
                  </div>

                  <div>
                    <label>Claim Amount (€)</label>
                    <TextBox
                      inputAttr={{ type: "number", step: "any", min: "1" }} // Allow decimal values
                      value={values.claimAmount}
                      onValueChanged={(e) =>
                        setFieldValue("claimAmount", e.value)
                      }
                      isValid={!(errors.claimAmount && touched.claimAmount)} // Show validation only if touched
                      validationError={
                        errors.claimAmount && touched.claimAmount
                          ? { message: "Enter a valid amount" }
                          : null
                      }
                    />
                  </div>
                </div>
              </div>
              {/* <div className="grid grid-cols-4 gap-4">
                          

                            
                        </div> */}

              {/* Section: Product */}
              <div className="dx-card p-6 custom-base-accent">
                <div className="row mt-4 mb-2">
                  <div className="col-md-12 flex items-center">
                    <h6 className="whitespace-nowrap">Product:</h6>
                    <div className="flex-grow h-px bg-gray-300 ml-2 light-bg"></div>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Product Name */}
                  <div>
                    <label className="relative">
                      Product Name{" "}
                      <span className="text-red-500 text-lg absolute top-[-4px] right-[-10px]">
                        *
                      </span>
                    </label>
                    <SelectBox
                      dataSource={products?.data || []}
                      displayExpr="name"
                      valueExpr="productID"
                        searchEnabled={true} // Enable search
                      showClearButton={true} // Allow deselection
                      value={values.productID} // Should match valueExpr
                      onValueChanged={(e) => {
                        setFieldValue("productID", e.value);
                        setProductID(e.value); // This triggers fetching sizes and qualities
                      }}
                      isValid={!(errors.productID && touched.productID)}
                      validationError={
                        errors.productID ? { message: errors.productID } : null
                      }
                    />
                  </div>

                  <div>
                    <label className="relative">Product Size (kg)</label>
                    <SelectBox
                      dataSource={sizes?.data || []}
                      displayExpr="description"
                      valueExpr="productSizeID"
                      value={values.productSizeID}
                      onValueChanged={(e) =>
                        setFieldValue("productSizeID", e.value)
                      }
                    //   isValid={!(errors.productSizeID && touched.productSizeID)}
                    //   validationError={
                    //     errors.productSizeID
                    //       ? { message: errors.productSizeID }
                    //       : null
                    //   }
                    />
                  </div>
                  <div>
                    <label className="relative">Quality</label>
                    <SelectBox
                      dataSource={qualities?.data || []}
                      displayExpr="description"
                      valueExpr="productQualityID"
                      value={values.productQualityID}
                      onValueChanged={(e) =>
                        setFieldValue("productQualityID", e.value)
                      }
                    //   isValid={
                    //     !(errors.productQualityID && touched.productQualityID)
                    //   }
                    //   validationError={
                    //     errors.productQualityID
                    //       ? { message: errors.productQualityID }
                    //       : null
                    //   }
                    />
                  </div>
                  <div>
                    <label>Sale Price / kg (€)</label>
                    <TextBox
                      value={values.salePricePerKG}
                      inputAttr={{ type: "number", step: "any", min: "1" }} // Allow decimal values
                      // Restricts to numbers only

                      onValueChanged={(e) =>
                        setFieldValue("salePricePerKG", e.value)
                      }
                      // Show validation messages as tooltips
                      isValid={
                        !(errors.salePricePerKG && touched.salePricePerKG)
                      } // Controls border color
                      validationError={
                        errors.salePricePerKG
                          ? { message: errors.salePricePerKG }
                          : null
                      }
                    />
                  </div>

                  {/* Location Dropdown */}
                  <div>
                    <label className="relative">
                      Location{" "}
                      {/* <span className="text-red-500 text-lg absolute top-[-4px] right-[-10px]">
                        *
                      </span> */}
                    </label>
                    <SelectBox
                      dataSource={values.availableLocations || []} // Use the dynamically set locations
                      displayExpr="addressLine" // Display the address line
                      valueExpr="supplierAddressID" // Use supplierAddressID as the value
                      value={values.locationID}
                      onValueChanged={(e) => {
                        console.log("Selected Location ID:", e.value); // Debug log for selected location
                        setFieldValue("locationID", e.value);
                      }}
                    //   isValid={!(errors.locationID && touched.locationID)}
                    //   validationError={
                    //     errors.locationID
                    //       ? { message: errors.locationID }
                    //       : null
                    //   }
                    />
                  </div>

                  <div>
                    <label>Purchase Ref.</label>
                    <TextBox
                      value={values.purchaseRef}
                      onValueChanged={(e) =>
                        setFieldValue("purchaseRef", e.value)
                      }
                      // Show validation messages as tooltips
                      // isValid={!(errors.claim && touched.claim)} // Controls border color
                      // validationError={errors.claim ? { message: errors.claim } : null} // Tooltip error message
                    />
                  </div>
                  <div>
                    <label className="relative">
                      <span className="text-red-500 text-lg absolute top-[-4px] right-[-10px]">
                        *
                      </span>
                      Batch No.
                    </label>
                    <TextBox
                      value={values.batchNumber}
                      onValueChanged={(e) =>
                        setFieldValue("batchNumber", e.value)
                      }
                      isValid={!(errors.batchNumber && touched.batchNumber)} // Controls border color
                      validationError={
                        errors.batchNumber
                          ? { message: errors.batchNumber }
                          : null
                      } // Tooltip error message
                    />
                  </div>
                </div>
              </div>
              {/* Buttons */}
              <div className="flex mt-6 justify-end">
                <Button
                  className="me-3"
                  width={100}
                  text="Cancel"
                  type="normal"
                  stylingMode="outlined"
                  onClick={() => {
                    navigate("/quality/fillet/overview");
                  }}
                />
                <Button
                  width={100}
                  text={loading ? "Updating..." : "Update"} // Change text when loading
                  stylingMode="contained"
                  type="default"
                  useSubmitBehavior
                  onClick={() => {
                    console.log(`values`, errors);
                  }}
                  disabled={loading} // Disable button when loading
                />
              </div>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default EditBatchIntake;
